import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getKioskClockOutDataRequest, KIOSK_NO_ACTIVE_REGISTRATION } from 'api/requests/kiosk-session-requests';
import { fromResponseToKioskClockOutData } from 'mappers/kiosk-clock-out-data-mapper';
import kioskSessionService from 'services/kiosk-session-service';
import { TKioskClockOutData } from 'types/kiosk/TKioskClockOutData';
import { pickKioskPolicies } from 'utils/kiosk-language';
import { useEndKioskSession } from './useEndKioskSession';

export type TKioskClockOutStatus = 'idle' | 'loading' | 'ready' | 'no-active' | 'unavailable' | 'error';

/**
 * Fetches the clock-out prefill for the tapped employee.
 * 204 → 'no-active' (route to clock-in). 401 → dead session (gate drops to INACTIVE).
 * 403 EMPLOYEE_NOT_AT_LOCATION → 'unavailable'. Any other failure → 'error'.
 * employeeId = null resets the hook (used while the modal is closed).
 */
export const useKioskClockOutData = (employeeId: number | null) => {
  const endSession = useEndKioskSession();
  const [data, setData] = useState<TKioskClockOutData | null>(null);
  const [status, setStatus] = useState<TKioskClockOutStatus>('idle');
  const isMounted = useRef(true);
  const { i18n } = useTranslation();

  const fetchData = useCallback(
    async (id: number) => {
      const token = kioskSessionService.getToken();
      if (!token) {
        endSession();
        return;
      }
      setStatus('loading');
      setData(null);
      try {
        const result = await getKioskClockOutDataRequest(token, id);
        if (!isMounted.current) return;
        if (result === KIOSK_NO_ACTIVE_REGISTRATION) {
          setStatus('no-active');
          return;
        }
        const mapped = fromResponseToKioskClockOutData(result);
        if (!mapped) {
          setStatus('error');
          return;
        }
        setData(mapped);
        setStatus('ready');
      } catch (e) {
        if (!isMounted.current) return;
        const httpStatus = (e as { response?: { status?: number } })?.response?.status;
        const code = (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? '';
        if (httpStatus === 401) {
          endSession();
          return;
        }
        setStatus(httpStatus === 403 && code.includes('EMPLOYEE_NOT_AT_LOCATION') ? 'unavailable' : 'error');
      }
    },
    [endSession],
  );

  useEffect(() => {
    isMounted.current = true;
    if (employeeId != null) {
      fetchData(employeeId);
    } else {
      setData(null);
      setStatus('idle');
    }
    return () => {
      isMounted.current = false;
    };
  }, [employeeId, fetchData]);

  // The policy text comes in every kiosk language, so a language switch re-renders it from `data` in place:
  // no request, and `data` stays the same object, so the view's form prefill does not re-run.
  const policies = useMemo(
    () => (data ? pickKioskPolicies(data.policiesByLanguage, data.policies, i18n.language) : []),
    [data, i18n.language],
  );

  return { data, status, policies };
};
